import React, { Component } from "react";
import { Link } from "react-router-dom";
import Asset from "./Asset";
import {
  authenticatedGet,
  authenticatedPost,
  authenticatedDelete,
} from "../utils/apiUtils";
import "../utils/authTest"; // Load the auth test utility
import QRCodeDisplay from "./QRCodeDisplay";
import { Alert, Button } from "reactstrap";
import Swal from "sweetalert2";

export class Home extends Component {
  static displayName = Home.name;

  constructor(props) {
    super(props);
    this.state = {
      assets: [],
      showProperties: {},
      selectedAssetForQR: null,
      token: "",
      loading: true,
      encryptionStatus: null,
      showEncryptionAlert: true,
      requiresAuthentication: false,
      password: "",
      showPasswordModal: false,
      rememberSession: false,
      sessionExpiry: null,
      showSendQRModal: false,
      sendQRRecipient: "",
      sendQRAssetId: null,
      selectedAssets: {}, // For PDF report generation
      showReportOptions: false,
    };
  }

  async componentDidMount() {
    try {
      this.setState({ loading: true });

      // Check if we're being redirected due to session expiration
      const urlParams = new URLSearchParams(window.location.search);
      const requiresAuth = urlParams.get("requiresAuth") === "true";

      // Check encryption status first
      await this.checkEncryptionStatus();

      // Check for active session (only for Enhanced/Maximum users)
      await this.checkSessionStatus();

      // If redirected due to auth requirement, show password modal immediately
      if (requiresAuth) {
        this.setState({
          requiresAuthentication: true,
          showPasswordModal: true,
        });
        // Clean up the URL parameter
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      }

      // Try to fetch assets
      await this.fetchAssets();
    } catch (error) {
      console.error("Error in componentDidMount:", error);
      this.setState({ loading: false });
    }
  }

  fetchAssets = async () => {
    try {
      this.setState({ loading: true });

      const response = await authenticatedGet("/assets");

      if (response.ok) {
        const data = await response.json();
        this.setState({
          assets: data,
          loading: false,
          requiresAuthentication: false,
          showPasswordModal: false,
          password: "",
        });
      } else {
        const errorData = await response.json();
        if (errorData.requiresAuthentication) {
          // Session authentication is required
          console.log("Authentication required, showing password modal");
          this.setState({
            requiresAuthentication: true,
            showPasswordModal: true,
            loading: false,
          });
        } else {
          console.error("Failed to fetch assets:", response.status, errorData);
          this.setState({ loading: false });
        }
      }
    } catch (error) {
      console.error("Error fetching assets:", error);
      this.setState({ loading: false });
    }
  };

  authenticateSession = async (password, rememberSession = false) => {
    try {
      const response = await authenticatedPost(
        "/encryption/authenticate-session",
        {
          password,
          rememberSession,
        }
      );

      if (response.ok) {
        const data = await response.json();
        this.setState({
          showPasswordModal: false,
          password: "",
          sessionExpiry: data.expiresIn,
        });

        // Now fetch assets with authenticated session
        await this.fetchAssets();

        return { success: true, message: data.message };
      } else {
        const errorData = await response.json();
        return { success: false, message: errorData.message };
      }
    } catch (error) {
      console.error("Error authenticating session:", error);
      return { success: false, message: "Authentication failed" };
    }
  };

  invalidateSession = async () => {
    try {
      await authenticatedPost("/encryption/invalidate-session", {});
      this.setState({
        assets: [],
        requiresAuthentication: true,
        showPasswordModal: true,
        sessionExpiry: null,
      });
    } catch (error) {
      console.error("Error invalidating session:", error);
    }
  };

  checkEncryptionStatus = async () => {
    try {
      const response = await authenticatedGet("/encryption/security-level");
      if (response.ok) {
        const data = await response.json();
        this.setState({ encryptionStatus: data.securityLevel });
      } else {
        // No security level set - new user
        this.setState({ encryptionStatus: null });
      }
    } catch (error) {
      console.error("Error checking encryption status:", error);
      this.setState({ encryptionStatus: null });
    }
  };

  checkSessionStatus = async () => {
    try {
      const response = await authenticatedGet("/encryption/session-status");
      if (response.ok) {
        const data = await response.json();
        if (data.hasActiveSession) {
          this.setState({
            sessionExpiry: data.expiresIn,
            requiresAuthentication: false,
            showPasswordModal: false,
          });
        } else {
          // No active session for Enhanced/Maximum users
          if (
            data.securityLevel === "Enhanced" ||
            data.securityLevel === "Maximum"
          ) {
            this.setState({
              sessionExpiry: null,
              requiresAuthentication: true,
              showPasswordModal: true, // Show the modal immediately for Enhanced/Maximum without active session
            });
          }
        }
      }
    } catch (error) {
      console.error("Error checking session status:", error);
      // Don't set state here - let fetchAssets handle the session check
    }
  };

  toggleProperties = (assetId) => {
    this.setState((prevState) => ({
      showProperties: {
        ...prevState.showProperties,
        [assetId]: !prevState.showProperties[assetId],
      },
    }));
  };

  generateQRCodeForAsset = (assetId) => {
    this.setState({ selectedAssetForQR: assetId });
  };

  handleDeleteAsset = (assetId, assetName, event) => {
    // Prevent the click from navigating to the asset details page
    event.stopPropagation();

    Swal.fire({
      title: "Are you sure?",
      text: `You are about to delete "${assetName}". This action cannot be undone!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await authenticatedDelete(`/assets/${assetId}`);

          if (response.ok) {
            // Remove the deleted asset from the state
            const updatedAssets = this.state.assets.filter(
              (asset) => asset.id !== assetId
            );
            this.setState({ assets: updatedAssets });

            Swal.fire({
              icon: "success",
              title: "Deleted!",
              text: "The item has been deleted successfully.",
              timer: 2000,
              showConfirmButton: false,
            });
          } else {
            // Check if this is a session expiration error
            const errorData = await response.json().catch(() => null);
            if (errorData?.requiresAuthentication) {
              this.setState({
                requiresAuthentication: true,
                showPasswordModal: true,
              });
            } else {
              throw new Error("Failed to delete item");
            }
          }
        } catch (error) {
          console.error("Delete error:", error);
          Swal.fire({
            icon: "error",
            title: "Error!",
            text: "There was a problem deleting the item.",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  handlePasswordChange = (e) => {
    this.setState({ password: e.target.value });
  };

  handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (this.state.password) {
      this.setState({ loading: true });
      const result = await this.authenticateSession(
        this.state.password,
        this.state.rememberSession
      );

      if (!result.success) {
        // Don't show alert - just reset loading state and let the system reprompt
        this.setState({ loading: false });
      }
      // If successful, fetchAssets is called automatically by authenticateSession
    }
  };

  handleRememberSessionChange = (e) => {
    this.setState({ rememberSession: e.target.checked });
  };

  handlePasswordCancel = () => {
    this.setState({
      showPasswordModal: false,
      password: "",
      loading: false,
      requiresAuthentication: false, // Respect the user's choice to not authenticate
    });
  };

  // --- QR Email Modal Handlers ---
  openSendQRModal = (assetId) => {
    this.setState({
      showSendQRModal: true,
      sendQRAssetId: assetId,
      sendQRRecipient: "",
    });
  };

  closeSendQRModal = () => {
    this.setState({
      showSendQRModal: false,
      sendQRAssetId: null,
      sendQRRecipient: "",
    });
  };

  handleSendQRRecipientChange = (e) => {
    this.setState({ sendQRRecipient: e.target.value });
  };

  handleSendQR = async () => {
    const { sendQRAssetId, sendQRRecipient } = this.state;
    if (!sendQRRecipient.trim()) {
      Swal.fire({
        icon: "error",
        title: "Recipient Required",
        text: "Please enter a username or email.",
      });
      return;
    }
    try {
      const response = await authenticatedPost("/assets/send-qr", {
        assetId: sendQRAssetId,
        recipient: sendQRRecipient.trim(),
      });
      if (response.ok) {
        Swal.fire({
          icon: "success",
          title: "QR Sent",
          text: "The QR code was sent successfully.",
        });
        this.closeSendQRModal();
      } else {
        let errorMsg = "Failed to send QR code.";
        try {
          const errorData = await response.json();
          if (errorData && errorData.message) errorMsg = errorData.message;
        } catch {}
        Swal.fire({ icon: "error", title: "Send Failed", text: errorMsg });
      }
    } catch (error) {
      let errorMsg = "Failed to send QR code.";
      if (error && error.response) {
        try {
          const errorData = await error.response.json();
          if (errorData && errorData.message) errorMsg = errorData.message;
        } catch {}
      } else if (error && error.message) {
        errorMsg = error.message;
      }
      Swal.fire({ icon: "error", title: "Send Failed", text: errorMsg });
    }
  };

  // --- PDF Report Handlers ---
  toggleReportMode = () => {
    this.setState((prevState) => ({
      showReportOptions: !prevState.showReportOptions,
      selectedAssets: {}, // Clear selections when toggling
    }));
  };

  toggleAssetSelection = (assetId) => {
    this.setState((prevState) => ({
      selectedAssets: {
        ...prevState.selectedAssets,
        [assetId]: !prevState.selectedAssets[assetId],
      },
    }));
  };

  selectAllAssets = () => {
    const { assets } = this.state;
    const selectedAssets = {};
    assets.forEach((asset) => {
      selectedAssets[asset.id] = true;
    });
    this.setState({ selectedAssets });
  };

  clearAllSelections = () => {
    this.setState({ selectedAssets: {} });
  };

  generateReport = async () => {
    const { selectedAssets } = this.state;
    const assetIds = Object.keys(selectedAssets)
      .filter((id) => selectedAssets[id])
      .map(Number);

    if (assetIds.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Items Selected",
        text: "Please select at least one item for the report.",
      });
      return;
    }

    try {
      // Send client's local timestamp to backend
      // Format as a string that represents local time, not UTC
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const seconds = String(now.getSeconds()).padStart(2, "0");
      const clientTimestamp = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;

      const response = await authenticatedPost("/assets/generate-report", {
        assetIds,
        clientTimestamp,
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;

        // Extract username from Content-Disposition header if available
        let username = "User";
        const contentDisposition = response.headers.get("Content-Disposition");
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(
            /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
          );
          if (filenameMatch && filenameMatch[1]) {
            const backendFilename = filenameMatch[1].replace(/['"]/g, "");
            // Extract username from filename pattern: Amnathrig_Item_Report_{username}_{timestamp}.pdf
            const usernameMatch = backendFilename.match(
              /Amnathrig_Item_Report_([^_]+)_/
            );
            if (usernameMatch && usernameMatch[1]) {
              username = usernameMatch[1];
            }
          }
        }

        // Generate filename with user's local timestamp
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, "0");
        const day = String(now.getDate()).padStart(2, "0");
        const hours = String(now.getHours()).padStart(2, "0");
        const minutes = String(now.getMinutes()).padStart(2, "0");
        const seconds = String(now.getSeconds()).padStart(2, "0");
        const timestamp = `${year}${month}${day}_${hours}${minutes}${seconds}`;
        const filename = `Amnathrig_Item_Report_${username}_${timestamp}.pdf`;

        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        Swal.fire({
          icon: "success",
          title: "Report Generated",
          text: "Your PDF report has been downloaded.",
        });
        this.setState({ showReportOptions: false, selectedAssets: {} });
      } else {
        const errorData = await response.json().catch(() => null);
        if (errorData?.requiresAuthentication) {
          this.setState({
            requiresAuthentication: true,
            showPasswordModal: true,
          });
          Swal.fire({
            icon: "error",
            title: "Authentication Required",
            text: "Session authentication is required to generate a report with encrypted items.",
          });
        } else {
          throw new Error(errorData?.message || "Failed to generate report");
        }
      }
    } catch (error) {
      console.error("Report generation error:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Failed to generate report.",
      });
    }
  };

  render() {
    const {
      assets,
      showProperties,
      selectedAssetForQR,
      token,
      loading,
      encryptionStatus,
      showEncryptionAlert,
      showPasswordModal,
      password,
      requiresAuthentication,
      rememberSession,
      sessionExpiry,
    } = this.state;

    if (loading) {
      return (
        <div className="home-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading your assets...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="home-container">
        {/* Encryption Security Alert */}
        {encryptionStatus === null && showEncryptionAlert && (
          <Alert
            color="info"
            toggle={() => this.setState({ showEncryptionAlert: false })}
          >
            <h4 className="alert-heading">
              <i className="fas fa-shield-alt me-2"></i>
              Secure Your Items with Encryption
            </h4>
            <p>
              Your items are currently stored without encryption. Set up
              encryption to enhance your data security and take advantage of our
              three-tier security system.
            </p>
            <div className="d-flex gap-2">
              <Button
                tag={Link}
                to="/encryption-settings"
                color="primary"
                size="sm"
              >
                <i className="fas fa-lock me-1"></i>
                Set Up Encryption
              </Button>
              <Button
                color="secondary"
                outline
                size="sm"
                onClick={() => this.setState({ showEncryptionAlert: false })}
              >
                Remind Me Later
              </Button>
            </div>
          </Alert>
        )}

        <div className="hero-section">
          <h1 className="hero-title">Item Registry</h1>
          <p className="hero-subtitle">
            Manage, track, and organize your items with ease
          </p>

          {assets.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon">📦</div>
              <h3>No Items Yet</h3>
              <p>Start by registering your first item to get organized</p>
              <Link to="/create-asset" className="btn btn-primary btn-lg">
                <span className="btn-icon">+</span>
                Register Your First Item
              </Link>
              {(encryptionStatus === "Enhanced" ||
                encryptionStatus === "Maximum") && (
                <div className="mt-3">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => this.setState({ showPasswordModal: true })}
                  >
                    <i className="fas fa-key me-2"></i>
                    Authenticate Session
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {assets.length > 0 && (
          <>
            {/* Session Management */}
            {(encryptionStatus === "Enhanced" ||
              encryptionStatus === "Maximum") &&
              this.state.sessionExpiry && (
                <div className="alert alert-success d-flex justify-content-between align-items-center">
                  <div>
                    <i className="fas fa-shield-check me-2"></i>
                    Session authenticated - expires in{" "}
                    {this.state.sessionExpiry} minutes
                  </div>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={this.invalidateSession}
                  >
                    <i className="fas fa-sign-out-alt me-1"></i>
                    End Session
                  </button>
                </div>
              )}

            <div className="assets-header">
              <div className="assets-count">
                <span className="count-number">{assets.length}</span>
                <span className="count-label">
                  Item{assets.length !== 1 ? "s" : ""}
                </span>
              </div>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button
                  onClick={this.toggleReportMode}
                  className={`btn ${
                    this.state.showReportOptions
                      ? "btn-secondary"
                      : "btn-outline-secondary"
                  }`}
                >
                  <span className="btn-icon">📄</span>
                  {this.state.showReportOptions
                    ? "Cancel Report"
                    : "Generate Report"}
                </button>
                <Link to="/create-asset" className="btn btn-primary">
                  <span className="btn-icon">+</span>
                  New Item
                </Link>
              </div>
            </div>

            {/* Report Options */}
            {this.state.showReportOptions && (
              <div className="alert alert-info" style={{ marginTop: "1rem" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "1rem",
                  }}
                >
                  <h5 style={{ margin: 0 }}>
                    <i className="fas fa-check-square me-2"></i>
                    Select items for report (
                    {
                      Object.values(this.state.selectedAssets).filter(Boolean)
                        .length
                    }{" "}
                    selected)
                  </h5>
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button
                      onClick={this.selectAllAssets}
                      className="btn btn-sm btn-outline-primary"
                    >
                      Select All
                    </button>
                    <button
                      onClick={this.clearAllSelections}
                      className="btn btn-sm btn-outline-secondary"
                    >
                      Clear All
                    </button>
                    <button
                      onClick={this.generateReport}
                      className="btn btn-sm btn-success"
                      disabled={
                        Object.values(this.state.selectedAssets).filter(Boolean)
                          .length === 0
                      }
                    >
                      <i className="fas fa-file-pdf me-1"></i>
                      Generate PDF
                    </button>
                  </div>
                </div>
                <p style={{ margin: 0, fontSize: "0.9rem" }}>
                  Click on items below to select them for the report. The report
                  will include a cover page and detailed information for each
                  selected item.
                </p>
              </div>
            )}

            <div className="assets-grid">
              {assets.map((asset) => (
                <div
                  key={asset.id}
                  className="asset-card"
                  style={{ position: "relative" }}
                >
                  {/* Report Selection Checkbox */}
                  {this.state.showReportOptions && (
                    <div
                      style={{
                        position: "absolute",
                        top: 10,
                        left: 10,
                        zIndex: 10,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!!this.state.selectedAssets[asset.id]}
                        onChange={() => this.toggleAssetSelection(asset.id)}
                        style={{ width: 20, height: 20, cursor: "pointer" }}
                      />
                    </div>
                  )}

                  <div className="asset-header">
                    <h3 className="asset-title">
                      <Link
                        to={`/asset-details/${asset.id}`}
                        className="asset-link"
                      >
                        {asset.name}
                      </Link>
                    </h3>
                    <span className="asset-type-badge">{asset.assetType}</span>
                  </div>

                  <p className="asset-description">{asset.description}</p>

                  <div
                    className="asset-actions"
                    style={{ flexDirection: "column" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "0.5rem",
                        width: "100%",
                      }}
                    >
                      <button
                        onClick={() => this.generateQRCodeForAsset(asset.id)}
                        className="btn btn-outline"
                        title="Generate QR Code"
                      >
                        <span className="btn-icon">📱</span>
                        QR Code
                      </button>
                      <button
                        onClick={() => this.toggleProperties(asset.id)}
                        className="btn btn-outline"
                        title="Toggle Item Details"
                      >
                        <span className="btn-icon">🔍</span>
                        {showProperties[asset.id]
                          ? "Hide Details"
                          : "Show Details"}
                      </button>
                    </div>
                    <div style={{ width: "100%", marginTop: "0.75rem" }}>
                      <button
                        onClick={() => this.openSendQRModal(asset.id)}
                        className="btn btn-outline"
                        title="Send QR Code via Email"
                        style={{ width: "100%" }}
                      >
                        <span className="btn-icon">✉️</span>
                        Email QR Code
                      </button>
                    </div>
                  </div>

                  <div className="delete-action">
                    <button
                      onClick={(e) =>
                        this.handleDeleteAsset(asset.id, asset.name, e)
                      }
                      className="btn btn-outline-danger btn-sm"
                      title="Delete This Item"
                    >
                      <span className="btn-icon">🗑️</span>
                      Delete Item
                    </button>
                  </div>

                  {showProperties[asset.id] && (
                    <div className="asset-properties">
                      <Asset asset={asset} />
                    </div>
                  )}

                  {selectedAssetForQR === asset.id && (
                    <div className="qr-code-section">
                      <h4>QR Code for {asset.name}</h4>
                      <QRCodeDisplay
                        content={`${asset.userId}:${asset.id}`}
                        token={token}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Password Modal */}
        {showPasswordModal && (
          <div
            className="modal fade show"
            style={{ display: "block" }}
            tabIndex="-1"
          >
            {console.log("Rendering password modal")}
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="fas fa-key me-2"></i>
                    Session Authentication Required
                  </h5>
                </div>
                <div className="modal-body">
                  <p>
                    Your account uses Enhanced or Maximum security. Please
                    authenticate your session to view your items.
                  </p>
                  <form onSubmit={this.handlePasswordSubmit}>
                    <div className="form-group mb-3">
                      <label htmlFor="password">Password:</label>
                      <input
                        type="password"
                        className="form-control"
                        id="password"
                        value={password}
                        onChange={this.handlePasswordChange}
                        required
                        autoFocus
                      />
                    </div>
                    <div className="form-check mb-3">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="rememberSession"
                        checked={this.state.rememberSession}
                        onChange={this.handleRememberSessionChange}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="rememberSession"
                      >
                        Remember this session for 8 hours (instead of 30
                        minutes)
                      </label>
                    </div>
                  </form>
                  {this.state.sessionExpiry && (
                    <div className="alert alert-info">
                      <small>
                        Session will expire in {this.state.sessionExpiry}{" "}
                        minutes
                      </small>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={this.handlePasswordCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={this.handlePasswordSubmit}
                  >
                    <i className="fas fa-unlock me-1"></i>
                    Authenticate Session
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {showPasswordModal && <div className="modal-backdrop fade show"></div>}

        {/* Send QR Modal */}
        {this.state.showSendQRModal && (
          <div
            className="modal show"
            style={{ display: "block" }}
            tabIndex="-1"
          >
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Send QR Code via Email</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={this.closeSendQRModal}
                  ></button>
                </div>
                <div className="modal-body">
                  <label htmlFor="sendQRRecipient">
                    Recipient Username or Email:
                  </label>
                  <input
                    type="text"
                    id="sendQRRecipient"
                    className="form-control"
                    value={this.state.sendQRRecipient}
                    onChange={this.handleSendQRRecipientChange}
                    placeholder="Enter username or email"
                  />
                </div>
                <div className="modal-footer">
                  <Button color="secondary" onClick={this.closeSendQRModal}>
                    Cancel
                  </Button>
                  <Button color="primary" onClick={this.handleSendQR}>
                    Send QR Code
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        {this.state.showSendQRModal && (
          <div className="modal-backdrop fade show"></div>
        )}
      </div>
    );
  }
}
